/**
 * /intl-breeders/apply — Foreign breeder applies to list.
 * /intl-breeders/me — Foreign breeder's status + checkout/renew CTA.
 *
 * Single file; routed to two URLs in App.js.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircleIcon as CheckCircle, GlobeHemisphereWestIcon as GlobeHemisphereWest } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

export default function IntlBreederApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kennelName, setKennelName] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [breeds, setBreeds] = useState("");
  const [website, setWebsite] = useState("");
  const [bio, setBio] = useState("");
  const [years, setYears] = useState("");
  const [shipsIntl, setShipsIntl] = useState(true);
  const [breedingRights, setBreedingRights] = useState(false);
  const [venueAck, setVenueAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const paidBanner = searchParams.get("paid"); // "ok" | "cancel" | null

  useEffect(() => {
    if (!user) { navigate("/login?next=/intl-breeders/apply"); return; }
    api.get("/me/intl-breeders/me")
      .then((r) => {
        const row = r.data?.listing;
        if (row) {
          setExisting(row);
          setKennelName(row.kennel_name || "");
          setCountry(row.country || "");
          setCity(row.city || "");
          setBreeds((row.breeds || []).join(", "));
          setWebsite(row.website || "");
          setBio(row.bio || "");
          setYears(row.years_breeding ? String(row.years_breeding) : "");
          setShipsIntl(Boolean(row.intl_shipping_supported));
          setBreedingRights(Boolean(row.breeding_rights_offered));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const submit = async () => {
    if (!venueAck) { toast.error("Please acknowledge the venue-only terms."); return; }
    setSubmitting(true);
    try {
      await api.post("/me/intl-breeders/apply", {
        kennel_name: kennelName.trim(),
        country: country.trim().toUpperCase().slice(0, 2),
        city: city.trim(),
        breeds: breeds.split(",").map((s) => s.trim()).filter(Boolean),
        website: website.trim(),
        bio: bio.trim(),
        years_breeding: years ? Number(years) || null : null,
        intl_shipping_supported: shipsIntl,
        breeding_rights_offered: breedingRights,
        venue_acknowledged: true,
      });
      toast.success("Application saved.");
      const me = await api.get("/me/intl-breeders/me");
      setExisting(me.data?.listing);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startCheckout = async () => {
    setCheckoutBusy(true);
    try {
      const { data } = await api.post("/me/intl-breeders/checkout");
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCheckoutBusy(false);
    }
  };

  if (loading) {
    return <AppShell><div className="max-w-3xl mx-auto p-10 text-sm text-[#6B6B6B]">Loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="intl-breeder-apply">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Inbound Directory</p>
        <h1 className="font-spectral font-light text-4xl tracking-tight leading-[1.1] flex items-center gap-3">
          <GlobeHemisphereWest size={32} weight="duotone" className="text-gold"/>
          List your kennel for US buyers
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          $199 / 365 days. After payment we review your kennel (1–2 business days)
          and publish it in the public directory. US buyers email you directly;
          Armstrong does not hold money or arrange transport.
        </p>

        {paidBanner === "ok" && (
          <div className="rounded-xl border border-[#0F8043]/40 bg-[#EFF7EE] p-4 mt-6 flex items-start gap-3" data-testid="intl-breeder-paid-ok">
            <CheckCircle size={20} weight="fill" className="text-[#0F8043] flex-shrink-0"/>
            <div className="text-[13px] leading-relaxed">
              <p className="font-semibold text-[#0F8043]">Payment received.</p>
              <p className="text-[#3F3F3F] mt-0.5">
                Your listing has moved to admin review. We'll email you within
                1–2 business days when it goes live.
              </p>
            </div>
          </div>
        )}

        {existing?.status && (
          <div className="mt-6 rounded-xl border border-[#E5E5E5] bg-white p-5" data-testid="intl-breeder-status">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-1">Current status</p>
            <p className="font-semibold text-ink">
              {{
                pending_payment: "Draft saved — pay to submit for review.",
                pending_review: "Payment received · awaiting admin approval (1–2 business days).",
                approved: "Live in directory.",
                rejected: "Application rejected. Contact support if you'd like to revise.",
                removed_by_admin: "Removed by admin. Contact support.",
              }[existing.status] || existing.status}
            </p>
            {existing.paid_until && (
              <p className="text-[12px] text-[#6B6B6B] mt-1">
                Paid until: <strong>{new Date(existing.paid_until).toLocaleDateString()}</strong>
              </p>
            )}
            {(existing.status === "approved" || existing.status === "pending_review") && (
              <Button
                onClick={startCheckout}
                disabled={checkoutBusy}
                variant="outline"
                className="rounded-full mt-3"
                data-testid="intl-breeder-renew-btn"
              >
                {checkoutBusy ? "Redirecting…" : "Renew · $199 / 365 days"}
              </Button>
            )}
          </div>
        )}

        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Kennel name</Label>
            <Input value={kennelName} onChange={(e) => setKennelName(e.target.value)} className="mt-1.5" data-testid="intl-breeder-kennel-name"/>
          </div>
          <div className="grid grid-cols-[1fr_180px] gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Country (ISO-2)</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="DE" maxLength={2} className="mt-1.5 uppercase" data-testid="intl-breeder-country"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1.5" data-testid="intl-breeder-city"/>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Breeds (comma-separated)</Label>
            <Input value={breeds} onChange={(e) => setBreeds(e.target.value)} placeholder="Cane Corso, Rottweiler" className="mt-1.5" data-testid="intl-breeder-breeds"/>
          </div>
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Website</Label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://your-kennel.com" className="mt-1.5" data-testid="intl-breeder-website"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Years breeding</Label>
              <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} className="mt-1.5" data-testid="intl-breeder-years"/>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Bio / pitch</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)} className="mt-1.5 min-h-[140px]" placeholder="Bloodlines, working titles, health testing, what makes your kennel a fit for US buyers..." maxLength={4000} data-testid="intl-breeder-bio"/>
          </div>
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={shipsIntl} onChange={(e) => setShipsIntl(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="intl-breeder-ships-intl"/>
              <span>I ship internationally (buyer arranges courier)</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={breedingRights} onChange={(e) => setBreedingRights(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="intl-breeder-rights"/>
              <span>I offer full breeding rights</span>
            </label>
          </div>
          <label className="flex items-start gap-2 cursor-pointer text-[12px] leading-relaxed bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
            <input type="checkbox" checked={venueAck} onChange={(e) => setVenueAck(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="intl-breeder-venue-ack"/>
            <span>
              <strong>Venue acknowledgement.</strong> I understand Armstrong K9
              Registry is the venue only. I am the merchant of record for every
              sale; Armstrong does not screen me, hold any portion of the
              purchase price, or coordinate international transport. Buyers
              vet me themselves.
            </span>
          </label>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <Link to="/intl-breeders" className="text-[12px] text-[#6B6B6B] hover:text-crimson underline">
              ← Back to directory
            </Link>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={submit}
                disabled={submitting || !kennelName.trim() || !country.trim() || !bio.trim() || !venueAck}
                className="rounded-full"
                data-testid="intl-breeder-save-btn"
              >
                {submitting ? "Saving…" : existing ? "Update draft" : "Save draft"}
              </Button>
              {existing && existing.status === "pending_payment" && (
                <Button
                  onClick={startCheckout}
                  disabled={checkoutBusy}
                  className="rounded-full bg-ink hover:bg-crimson text-white"
                  data-testid="intl-breeder-pay-btn"
                >
                  {checkoutBusy ? "Redirecting…" : "Pay $199 & submit"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
